import {
  getFileTypesRoutine,
  removeFileRoutine,
  bulkRemoveFilesRoutine,
  updateFileRoutine,
  addFileVersionRoutine,
  getUploadProcessRoutine,
  saveUploadProcessFetchDetailsRoutine,
  clearUploadProcessFetchDetailsRoutine,
  clearUploadProcessRoutine,
  getBpFilesRoutine,
  getBpFilesCountsRoutine,
  getFilesStatisticsRoutine,
  loadFilesByFolderRoutine,
  loadFilesByCategoryRoutine,
  loadAllFilesRoutine,
  loadCustomFolderFilesRoutine,
  invalidateCustomFolderCacheRoutine,
  getFileTagsForBpRoutine,
  createFileTagRoutine,
  attachFileTagToFileRoutine,
  detachFileTagFromFileRoutine,
  syncFileTagsInFileRoutine,
  syncFileTagsInFileVersionRoutine,
  clearBpFilesRoutine,
  getRecentFilesRoutine,
  getRecommendedFileTypesRoutine,
  finalizeFileRoutine,
  restoreFileRoutine,
  shareFileRoutine,
  reclassifyFileRoutine,
  updateFileThumbnailsRoutine,
  updateLoadingProgressRoutine,
  cancelFileLoadingRoutine
} from 'ducks/files/actions'
import { pathOr } from 'ramda'
import emptyFilesState from 'ducks/files/state'
import { bulkShareFilesRoutine, fetchBpRoutine } from 'features/bpDetails/ducks/actions'

const normalizeFileName = name => {
  if (!name) return ''

  // First, simplify by lowercase
  let normalized = name.toLowerCase()

  // Remove common file extensions anywhere in the string
  normalized = normalized
    .replace(/\.pdf\.png$/i, '.') // Handle the specific .pdf.png case first
    .replace(/\.pdf/gi, '.') // Remove PDF extensions anywhere
    .replace(/\.(png|jpg|jpeg|doc|docx|xlsx|xls)$/i, '.') // Remove other extensions at the end
    .replace(/\./g, '') // Remove all remaining dots
    .replace(/[_\- ]/g, '') // Remove underscores, hyphens, and spaces

  return normalized
}
const UPDATABLE_FILE_FIELDS = ['displayName', 'description', 'floorNumbers']

const FILE_MUTATION_SUCCESS = [
  removeFileRoutine.SUCCESS,
  bulkRemoveFilesRoutine.SUCCESS,
  updateFileRoutine.SUCCESS,
  shareFileRoutine.SUCCESS,
  reclassifyFileRoutine.SUCCESS,
  finalizeFileRoutine.SUCCESS,
  restoreFileRoutine.SUCCESS
]

export default (state = emptyFilesState, action) => {
  if (FILE_MUTATION_SUCCESS.includes(action.type)) {
    state = { ...state, mutationVersion: (state.mutationVersion || 0) + 1 }
  }
  switch (action.type) {
    case addFileVersionRoutine.REQUEST:
      return {
        ...state,
        isUploading: true
      }
    case addFileVersionRoutine.FAILURE:
      return {
        ...state,
        isUploading: false
      }
    case addFileVersionRoutine.SUCCESS:
      return {
        ...state,
        isUploading: false
      }
    case getFileTypesRoutine.REQUEST:
      return {
        ...state,
        isLoadingFileTypes: true
      }

    case removeFileRoutine.SUCCESS: {
      const removedId = action.payload
      const customFolderFiles = Object.fromEntries(
        Object.entries(state.customFolderFiles || {}).map(([folderId, files]) => [
          folderId,
          Array.isArray(files) ? files.filter(f => f?.id !== removedId) : files
        ])
      )
      return {
        ...state,
        currentBpFiles: (state.currentBpFiles || []).filter(e => e.id !== removedId),
        bpFilesList: (state.bpFilesList || []).filter(e => e.id !== removedId),
        customFolderFiles
      }
    }
    case bulkShareFilesRoutine.SUCCESS: {
      const { fileIds = [], sharedWithUnits } = action.payload || {}
      if (!sharedWithUnits || !fileIds.length) return state
      const ids = new Set(fileIds)
      return {
        ...state,
        bpFilesList: (state.bpFilesList || []).map(f => (ids.has(f.id) ? { ...f, isSharedWithUnits: true } : f))
      }
    }
    case updateFileRoutine.SUCCESS: {
      const { id, body, file } = action.payload || {}
      if (!id || (!body && !file)) return state
      const patch = file || UPDATABLE_FILE_FIELDS.reduce((acc, key) => (body[key] !== undefined ? { ...acc, [key]: body[key] } : acc), {})
      if (!Object.keys(patch).length) return state
      const apply = f => (f?.id === id ? { ...f, ...patch } : f)
      return {
        ...state,
        bpFilesList: (state.bpFilesList || []).map(apply),
        customFolderFiles: Object.fromEntries(
          Object.entries(state.customFolderFiles || {}).map(([folderId, files]) => [
            folderId,
            Array.isArray(files) ? files.map(apply) : files
          ])
        )
      }
    }
    case bulkRemoveFilesRoutine.SUCCESS:
      // The folder/category loaders merge into bpFilesList and never drop entries,
      // so deleted files have to be removed here or they stay on screen.
      return {
        ...state,
        currentBpFiles: state.currentBpFiles.filter(
          e => !action.payload.includes(e.id)
        ),
        bpFilesList: state.bpFilesList.filter(
          e => !action.payload.includes(e.id)
        )
      }
    case getFileTypesRoutine.SUCCESS:
      return {
        ...state,
        isLoadingFileTypes: false,
        fileTypes: action.payload
      }
    case getFileTypesRoutine.FAILURE:
      return {
        ...state,
        isLoadingFileTypes: false
      }
    case fetchBpRoutine.SUCCESS:
      return {
        ...state,
        currentBpFiles: pathOr([], ['payload', 'files'], action)
      }


    case getUploadProcessRoutine.SUCCESS:
      // console.log('Processing upload process data:', action.payload)

      // Extract files from upload process and transform them to match multiUploadFiles structure
      const processFiles = action.payload.files || []

      if (processFiles.length > 0) {
        return {
          ...state,
          uploadProcess: action.payload,
          // Add or update files in multiUploadFiles
          multiUploadFiles: processFiles.map((file, index) => {
            // Try to find existing file in current multiUploadFiles
            const existingFile = state.multiUploadFiles.find(
              f => f.id === file.id
            )

            return {
              id: file.id,
              uniqueId: existingFile?.uniqueId || `${file.id}-${index}`,
              fileId: file.fileId || existingFile?.fileId || null,
              status: file.status || existingFile?.status || 'pending',
              storedFile: file.storedFile || existingFile?.storedFile || null,
              failureReason: file.failureReason ?? existingFile?.failureReason ?? null,
              displayName:
                file.data?.display_name ||
                file.data?.displayName ||
                existingFile?.displayName ||
                null
            }
          })
        }
      }

      // If no files in the process, just update the uploadProcess
      return {
        ...state,
        uploadProcess: action.payload
      }
    case clearUploadProcessRoutine.SUCCESS:
      return {
        ...state,
        uploadProcess: null
      }
    case saveUploadProcessFetchDetailsRoutine.SUCCESS:
      return {
        ...state,
        uploadProcessFetchDetails: action.payload
      }
    case clearUploadProcessFetchDetailsRoutine.SUCCESS:
      return {
        ...state,
        uploadProcessFetchDetails: null
      }
    case getBpFilesRoutine.REQUEST:
      return {
        ...state,
        isLoadingBpFiles: true
      }
    case getBpFilesRoutine.SUCCESS: {
      // Merge files: update existing files and add new ones
      const bpFilesPayload = action.payload?.files || action.payload || []
      const isPartialBp = action.payload?.partial
      const bpFilesMap = new Map(bpFilesPayload.map(f => [f.id, f]))
      const updatedBpFilesList = state.bpFilesList.map(f =>
        bpFilesMap.has(f.id) ? { ...f, ...bpFilesMap.get(f.id) } : f
      )
      const existingBpFileIds = new Set(state.bpFilesList.map(f => f.id))
      const newBpFiles = bpFilesPayload.filter(f => !existingBpFileIds.has(f.id))
      return {
        ...state,
        bpFilesList: [...updatedBpFilesList, ...newBpFiles],
        isLoadingBpFiles: isPartialBp ? true : false
      }
    }

    case getBpFilesCountsRoutine.SUCCESS:
      return {
        ...state,
        filesCounts: action.payload
      }
    case getFilesStatisticsRoutine.REQUEST:
      return {
        ...state,
        fileStatisticsLoading: true
      }
    case getFilesStatisticsRoutine.SUCCESS:
      return {
        ...state,
        fileStatistics: action.payload,
        fileStatisticsLoading: false
      }
    case getFilesStatisticsRoutine.FAILURE:
      return {
        ...state,
        fileStatisticsLoading: false
      }
    case getRecentFilesRoutine.SUCCESS:
      return {
        ...state,
        recentFiles: action.payload
      }
    case clearBpFilesRoutine.SUCCESS:
      return {
        ...state,
        bpFilesList: [],
        loadedFolders: new Set(),
        loadedCategories: new Set(),
        loadedCustomFolders: new Set(),
        allFilesLoaded: false,
        customFolderFiles: {}, // Clear folder files mapping
        thumbnailsById: {}, // Clear thumbnails
        loadingProgress: {}, // Clear progress
        cursorByKey: {},
        hasMoreByKey: {}
      }
    case getBpFilesRoutine.FAILURE:
      return {
        ...state,
        bpFilesList: [],
        isLoadingBpFiles: false
      }

    // Lazy loading by folder
    case loadFilesByFolderRoutine.REQUEST:
      return {
        ...state,
        loadingFolders: new Set([...state.loadingFolders, action.payload.folderId])
      }
    case loadFilesByFolderRoutine.SUCCESS: {
      const { folderId, files: folderFiles = [], nextCursor = null, hasMore = false } = action.payload
      const folderFilesMap = new Map(folderFiles.map(f => [f.id, f]))
      const updatedBpFilesListFolder = state.bpFilesList.map(f =>
        folderFilesMap.has(f.id) ? { ...f, ...folderFilesMap.get(f.id) } : f
      )
      const existingFileIds = new Set(state.bpFilesList.map(f => f.id))
      const newFolderFiles = folderFiles.filter(f => !existingFileIds.has(f.id))

      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(folderId)
      const folderSuccessProgress = { ...state.loadingProgress }
      delete folderSuccessProgress[folderId]

      return {
        ...state,
        bpFilesList: [...updatedBpFilesListFolder, ...newFolderFiles],
        loadedFolders: new Set([...state.loadedFolders, folderId]),
        loadingFolders: newLoadingFolders,
        loadingProgress: folderSuccessProgress,
        cursorByKey: { ...state.cursorByKey, [folderId]: nextCursor },
        hasMoreByKey: { ...state.hasMoreByKey, [folderId]: hasMore }
      }
    }
    case loadFilesByFolderRoutine.FAILURE:
      const failedFolderId = action.payload.folderId
      const failedLoadingFolders = new Set(state.loadingFolders)
      failedLoadingFolders.delete(failedFolderId)
      const failedFolderProgress = { ...state.loadingProgress }
      delete failedFolderProgress[failedFolderId]
      return {
        ...state,
        loadingFolders: failedLoadingFolders,
        loadingProgress: failedFolderProgress
      }

    // Lazy loading by category
    case loadFilesByCategoryRoutine.REQUEST:
      return {
        ...state,
        loadingCategories: new Set([...state.loadingCategories, action.payload.categoryId])
      }
    case loadFilesByCategoryRoutine.SUCCESS: {
      const { categoryId, files: categoryFiles = [], nextCursor = null, hasMore = false } = action.payload
      const categoryFilesMap = new Map(categoryFiles.map(f => [f.id, f]))
      const updatedBpFilesListCategory = state.bpFilesList.map(f =>
        categoryFilesMap.has(f.id) ? { ...f, ...categoryFilesMap.get(f.id) } : f
      )
      const existingCategoryFileIds = new Set(state.bpFilesList.map(f => f.id))
      const newCategoryFiles = categoryFiles.filter(f => !existingCategoryFileIds.has(f.id))
      const newLoadingCategories = new Set(state.loadingCategories)
      newLoadingCategories.delete(categoryId)
      const categorySuccessProgress = { ...state.loadingProgress }
      delete categorySuccessProgress[categoryId]

      return {
        ...state,
        bpFilesList: [...updatedBpFilesListCategory, ...newCategoryFiles],
        loadedCategories: new Set([...state.loadedCategories, categoryId]),
        loadingCategories: newLoadingCategories,
        loadingProgress: categorySuccessProgress,
        cursorByKey: { ...state.cursorByKey, [categoryId]: nextCursor },
        hasMoreByKey: { ...state.hasMoreByKey, [categoryId]: hasMore }
      }
    }
    case loadFilesByCategoryRoutine.FAILURE:
      const failedCategoryId = action.payload.categoryId
      const failedLoadingCategories = new Set(state.loadingCategories)
      failedLoadingCategories.delete(failedCategoryId)
      const failedCategoryProgress = { ...state.loadingProgress }
      delete failedCategoryProgress[failedCategoryId]
      return {
        ...state,
        loadingCategories: failedLoadingCategories,
        loadingProgress: failedCategoryProgress
      }

    // Load all files explicitly
    case loadAllFilesRoutine.REQUEST:
      return {
        ...state,
        isLoadingAllFiles: true
      }
    case loadAllFilesRoutine.SUCCESS: {
      const { files: allFilesPayload = [], nextCursor = null, hasMore = false } = action.payload
      const allFilesMap = new Map(allFilesPayload.map(f => [f.id, f]))
      const updatedBpFilesListAll = state.bpFilesList.map(f =>
        allFilesMap.has(f.id) ? { ...f, ...allFilesMap.get(f.id) } : f
      )
      const existingAllFileIds = new Set(state.bpFilesList.map(f => f.id))
      const newAllFiles = allFilesPayload.filter(f => !existingAllFileIds.has(f.id))

      const allSuccessProgress = { ...state.loadingProgress }
      delete allSuccessProgress['all']
      return {
        ...state,
        bpFilesList: [...updatedBpFilesListAll, ...newAllFiles],
        allFilesLoaded: !hasMore,
        isLoadingAllFiles: false,
        loadedFolders: new Set(['all']),
        loadedCategories: new Set(['all']),
        loadingProgress: allSuccessProgress,
        cursorByKey: { ...state.cursorByKey, all_files: nextCursor },
        hasMoreByKey: { ...state.hasMoreByKey, all_files: hasMore }
      }
    }
    case loadAllFilesRoutine.FAILURE:
      const failedAllProgress = { ...state.loadingProgress }
      delete failedAllProgress['all']
      return {
        ...state,
        isLoadingAllFiles: false,
        loadingProgress: failedAllProgress
      }

    // Lazy loading by custom folder
    case loadCustomFolderFilesRoutine.REQUEST:
      return {
        ...state,
        loadingCustomFolders: new Set([...state.loadingCustomFolders, action.payload.customFolderId])
      }
    case loadCustomFolderFilesRoutine.SUCCESS: {
      const customFolderFiles = action.payload.files || []
      const customFolderId = action.payload.customFolderId
      const isPartialCustom = action.payload.partial
      const folderAlreadyLoaded = state.loadedCustomFolders.has(customFolderId)
      // Merge files: update existing files and add new ones
      const customFolderFilesMap = new Map(customFolderFiles.map(f => [f.id, f]))
      const updatedBpFilesListCustom = state.bpFilesList.map(f =>
        customFolderFilesMap.has(f.id) ? { ...f, ...customFolderFilesMap.get(f.id) } : f
      )
      const existingCustomFolderFileIds = new Set(state.bpFilesList.map(f => f.id))
      const newCustomFolderFiles = customFolderFiles.filter(f => !existingCustomFolderFileIds.has(f.id))

      // Partial update: show files but keep loading state
      if (isPartialCustom || folderAlreadyLoaded) {
        return {
          ...state,
          bpFilesList: [...updatedBpFilesListCustom, ...newCustomFolderFiles],
          customFolderFiles: {
            ...state.customFolderFiles,
            [customFolderId]: customFolderFiles
          }
        }
      }

      // Final update: clear loading state
      const newLoadingCustomFolders = new Set(state.loadingCustomFolders)
      newLoadingCustomFolders.delete(customFolderId)
      const customFolderSuccessProgress = { ...state.loadingProgress }
      delete customFolderSuccessProgress[customFolderId]

      return {
        ...state,
        bpFilesList: [...updatedBpFilesListCustom, ...newCustomFolderFiles],
        loadedCustomFolders: new Set([...state.loadedCustomFolders, customFolderId]),
        loadingCustomFolders: newLoadingCustomFolders,
        customFolderFiles: {
          ...state.customFolderFiles,
          [customFolderId]: customFolderFiles
        },
        loadingProgress: customFolderSuccessProgress
      }
    }
    case loadCustomFolderFilesRoutine.FAILURE:
      const failedCustomFolderId = action.payload.customFolderId
      const failedLoadingCustomFolders = new Set(state.loadingCustomFolders)
      failedLoadingCustomFolders.delete(failedCustomFolderId)
      const failedCustomFolderProgress = { ...state.loadingProgress }
      delete failedCustomFolderProgress[failedCustomFolderId]
      return {
        ...state,
        loadingCustomFolders: failedLoadingCustomFolders,
        loadingProgress: failedCustomFolderProgress
      }

    // Invalidate custom folder cache for specific folders or all folders
    case invalidateCustomFolderCacheRoutine.TRIGGER:
      const { folderIds, invalidateAll } = action.payload || {}

      if (invalidateAll) {
        // Clear all custom folder caches
        return {
          ...state,
          loadedCustomFolders: new Set(),
          customFolderFiles: {}
        }
      }

      if (folderIds && folderIds.length > 0) {
        // Clear specific folders from cache
        const newLoadedCustomFolders = new Set(state.loadedCustomFolders)
        const newCustomFolderFiles = { ...state.customFolderFiles }

        folderIds.forEach(folderId => {
          const folderKey = folderId || 'unorganized'
          newLoadedCustomFolders.delete(folderKey)
          delete newCustomFolderFiles[folderKey]
        })

        return {
          ...state,
          loadedCustomFolders: newLoadedCustomFolders,
          customFolderFiles: newCustomFolderFiles
        }
      }

      return state

    case getRecentFilesRoutine.FAILURE:
      return {
        ...state,
        recentFiles: []
      }
    case getFileTagsForBpRoutine.REQUEST:
      return {
        ...state,
        isLoadingFileTags: true
      }
    case getFileTagsForBpRoutine.SUCCESS:
      return {
        ...state,
        isLoadingFileTags: false,
        fileTags: action.payload
      }
    case getFileTagsForBpRoutine.FAILURE:
      return {
        ...state,
        isLoadingFileTags: false
      }
    case createFileTagRoutine.SUCCESS:
      return {
        ...state,
        fileTags: [...state.fileTags, action.payload]
      }
    case attachFileTagToFileRoutine.SUCCESS:
    case detachFileTagFromFileRoutine.SUCCESS:
    case syncFileTagsInFileRoutine.SUCCESS:
      return {
        ...state,
        bpFilesList: state.bpFilesList.map(file => {
          if (file.id === action.payload.id) {
            return action.payload
          } else {
            return file
          }
        })
      }
    case syncFileTagsInFileVersionRoutine.SUCCESS:
      return {
        ...state,
        bpFilesList: state.bpFilesList.map(file => {
          if (file.id === action.payload.fileId) {
            const version = file?.versions || []
            return {
              ...file,
              versions: version.map(v => {
                if (v.id === action.payload.id) {
                  return action.payload
                } else {
                  return v
                }
              })
            }
          } else {
            return file
          }
        })
      }
    case getRecommendedFileTypesRoutine.SUCCESS:
      return {
        ...state,
        recommendedFileTypes: action.payload.list,
        recommendedFileTypesMessage: action.payload.message
      }
    case getRecommendedFileTypesRoutine.FAILURE:
      return {
        ...state,
        recommendedFileTypes: [],
        recommendedFileTypesMessage: null
      }

    // Optimistic update for file finalization (multi-upload completion)
    case finalizeFileRoutine.SUCCESS:
      // The payload contains the finalized file data
      if (action.payload && action.payload.file) {
        const finalized = action.payload.file
        return {
          ...state,
          bpFilesList: state.bpFilesList.some(f => f.id === finalized.id)
            ? state.bpFilesList.map(f => (f.id === finalized.id ? { ...f, ...finalized } : f))
            : [...state.bpFilesList, finalized]
        }
      }
      return state

    // Optimistic update for file sharing
    case shareFileRoutine.SUCCESS:
      // Update the file with new sharing information
      if (action.payload && action.payload.file) {
        return {
          ...state,
          bpFilesList: state.bpFilesList.map(file => {
            if (file.id === action.payload.file.id) {
              return action.payload.file
            }
            return file
          })
        }
      }
      return state

    // Optimistic update for file reclassification
    case reclassifyFileRoutine.SUCCESS:
      // Update the file's file type
      if (action.payload && action.payload.file) {
        return {
          ...state,
          bpFilesList: state.bpFilesList.map(file => {
            if (file.id === action.payload.file.id) {
              return action.payload.file
            }
            return file
          })
        }
      }
      return state

    // Thumbnail-only updates - stored separately to avoid re-rendering file lists
    case updateFileThumbnailsRoutine.SUCCESS:
      const thumbnailUpdates = action.payload || []
      const newThumbnails = {}
      thumbnailUpdates.forEach(f => {
        newThumbnails[f.id] = f.thumbnailUrl
      })
      return {
        ...state,
        thumbnailsById: {
          ...state.thumbnailsById,
          ...newThumbnails
        }
      }

    // Progress update (dispatched after each page fetch)
    case updateLoadingProgressRoutine.SUCCESS:
      return {
        ...state,
        loadingProgress: {
          ...state.loadingProgress,
          [action.payload.key]: { loaded: action.payload.loaded }
        }
      }

    // Cancellation cleanup
    case cancelFileLoadingRoutine.SUCCESS: {
      const { key, type } = action.payload
      const newProgress = { ...state.loadingProgress }
      delete newProgress[key]

      if (type === 'folder') {
        const s = new Set(state.loadingFolders)
        s.delete(key)
        return { ...state, loadingProgress: newProgress, loadingFolders: s }
      }
      if (type === 'category') {
        const s = new Set(state.loadingCategories)
        s.delete(key)
        return { ...state, loadingProgress: newProgress, loadingCategories: s }
      }
      if (type === 'all') {
        return { ...state, loadingProgress: newProgress, isLoadingAllFiles: false }
      }
      return { ...state, loadingProgress: newProgress }
    }

    default:
      return state
  }
}
